import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Loadingmock.css";
import { useLocation } from "react-router-dom";
import usePreventBack from "../hooks/usePreventBack";

// Each checklist item. "questions" and "ai" complete on a timer.
// "mic" only completes once real camera/mic permission is granted.
// "criteria" runs right after mic is granted.
const STEPS = [
  { id: "questions", label: "Selecting Questions" },
  { id: "ai", label: "Preparing AI Interviewer" },
  { id: "mic", label: "Checking Microphone" },
  { id: "criteria", label: "Creating Evaluation Criteria" },
];

const STEP1_AT = 650; // ms
const STEP2_AT = 1350; // ms, mic request starts right after this
const CRITERIA_DELAY = 500; // ms after mic is granted
const EXIT_DELAY = 400; // ms pause on 100% before navigating

export default function InterviewLoadingScreen({ onComplete }) {
   const location = useLocation();
usePreventBack(); 
  const message = location.state?.message;
  const navigate = useNavigate();
  const [doneMap, setDoneMap] = useState(() =>
    Object.fromEntries(STEPS.map((s) => [s.id, false]))
  );
  const [micStatus, setMicStatus] = useState("waiting"); // waiting | requesting | granted | denied | unsupported
  const [exiting, setExiting] = useState(false);
  const timeoutsRef = useRef([]);
  const streamRef = useRef(null);

  const requestMediaAccess = useCallback(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicStatus("unsupported");
      return;
    }

    setMicStatus("requesting");

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        streamRef.current = stream;
        setMicStatus("granted");
        setDoneMap((prev) => ({ ...prev, mic: true }));

        // Stop the tracks here since this screen doesn't show live video.
        // If your interview page needs the same stream, request it again
        // there, or lift this call up and pass the stream via router state.
        stream.getTracks().forEach((t) => t.stop());

        const t = setTimeout(() => {
          setDoneMap((prev) => ({ ...prev, criteria: true }));
          const exitTimer = setTimeout(() => setExiting(true), 250);
          const navTimer = setTimeout(() => {
            onComplete?.();
            navigate("/mockinterview",{
              state:{
                message: message
              }
            });
          }, 250 + EXIT_DELAY);
          timeoutsRef.current.push(exitTimer, navTimer);
        }, CRITERIA_DELAY);
        timeoutsRef.current.push(t);
      })
      .catch((err) => {
        setMicStatus(err?.name === "NotFoundError" ? "unsupported" : "denied");
      });
  }, [navigate, onComplete]);

  useEffect(() => {
    const t1 = setTimeout(() => {
      setDoneMap((prev) => ({ ...prev, questions: true }));
    }, STEP1_AT);

    const t2 = setTimeout(() => {
      setDoneMap((prev) => ({ ...prev, ai: true }));
      requestMediaAccess();
    }, STEP2_AT);

    timeoutsRef.current.push(t1, t2);

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedCount = STEPS.filter((s) => doneMap[s.id]).length;
  const activeStep = STEPS[completedCount] ?? null;
  const progress = Math.round((completedCount / STEPS.length) * 100);
  const blocked = micStatus === "denied" || micStatus === "unsupported";

  return (
    <div
      className={`ils-root${exiting ? " ils-root--exit" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="ils-scanlines" aria-hidden="true" />

      <div className="ils-panel">
        <div className="ils-header">
          <span className={`ils-dot${blocked ? " ils-dot--error" : ""}`} aria-hidden="true" />
          <h1 className="ils-title">
            Preparing your interview
            <span className="ils-cursor" aria-hidden="true">
              _
            </span>
          </h1>
        </div>

        <ul className="ils-checklist">
          {STEPS.map((step) => {
            const isDone = doneMap[step.id];
            const isActive = !isDone && activeStep?.id === step.id;
            const isMicBlocked = step.id === "mic" && blocked;
            return (
              <li
                key={step.id}
                className={`ils-step${isDone ? " ils-step--done" : ""}${
                  isActive && !isMicBlocked ? " ils-step--active" : ""
                }${isMicBlocked ? " ils-step--error" : ""}`}
              >
                <span className="ils-marker" aria-hidden="true">
                  {isDone ? (
                    <svg viewBox="0 0 16 16" className="ils-check">
                      <path
                        d="M3 8.5L6.2 11.5L13 4.5"
                        fill="none"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : isMicBlocked ? (
                    <svg viewBox="0 0 16 16" className="ils-error-icon">
                      <path
                        d="M8 4.5v4.2M8 11.2h.01"
                        fill="none"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  ) : isActive ? (
                    <span className="ils-spinner" />
                  ) : (
                    <span className="ils-pending" />
                  )}
                </span>
                <span className="ils-label">
                  {step.id === "mic" && micStatus === "requesting"
                    ? "Waiting for camera & mic permission…"
                    : step.label}
                </span>
              </li>
            );
          })}
        </ul>

        {blocked && (
          <div className="ils-permission-alert">
            <p className="ils-permission-text">
              {micStatus === "denied"
                ? "Camera and microphone access is required to continue. Please allow access in your browser's site settings, then try again."
                : "No camera or microphone was found on this device. Connect one and try again."}
            </p>
            <button
              type="button"
              className="ils-retry-btn"
              onClick={requestMediaAccess}
            >
              Try Again
            </button>
          </div>
        )}

        <div className="ils-progress-wrap">
          <div
            className="ils-progress-track"
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="ils-progress-fill"
              style={{ width: `${progress}%` }}
            />
            <div className="ils-progress-ticks" aria-hidden="true" />
          </div>
          <span className="ils-progress-readout">
            {String(progress).padStart(3, "0")}%
          </span>
        </div>
      </div>
    </div>
  );
}